// src/pages/projects/StagesPage.jsx
// Extracted from App.js - Stages page
// BRIO Migration: Using code "1a" (table default)
// HEADER ALIGNMENT FIX: Full-width header band with BrioFrame inside for centered content
import React, { useState } from 'react';
import { Plus, Pencil, Trash2, Check, X } from 'lucide-react';
import {
  BrioAnalyticsSurface,
  BrioByCode,
  BrioTable,
  BrioIconButton,
  BrioActionGroup,
  BrioFrame,
  getTablePropsFromCode
} from '../../components/BrioComponents';


// Presentation code for this page
const PRESENTATION_CODE = '1a';

export default function StagesPage({
  // Data
  activeTasks,
  tasks,
  currentUser,
  // Supabase
  useSupabaseDB,
  supabaseTasks,
  // State setters
  setTasks,
  setModal,
  setForm,
}) {
  const tableProps = getTablePropsFromCode(PRESENTATION_CODE);

  // ── Inline edit state ──
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editOrder, setEditOrder] = useState('');
  const [savingId, setSavingId] = useState(null);

  // ── Sort stages by order (fallback: id for defaults, Infinity for custom) ──
  const sortedStages = [...activeTasks].sort((a, b) => {
    const oa = a.order != null ? a.order : (a.id <= 16 ? a.id : 1000 + a.id);
    const ob = b.order != null ? b.order : (b.id <= 16 ? b.id : 1000 + b.id);
    return oa - ob;
  });

  // ── Start editing a stage ──
  const startEdit = (stage) => {
    setEditingId(stage.id);
    setEditName(stage.name);
    setEditOrder(stage.order != null ? stage.order : (stage.id <= 16 ? stage.id : ''));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditOrder('');
  };

  // ── Save edited stage ──
  const saveEdit = async (stage) => {
    const trimmedName = editName.trim();
    if (!trimmedName) return;
    const newOrder = editOrder !== '' ? parseInt(editOrder, 10) : null;

    setSavingId(stage.id);
    if (useSupabaseDB) {
      await supabaseTasks.updateTask(stage.id, { name: trimmedName, order: newOrder });
    } else {
      setTasks(tasks.map(t =>
        t.id === stage.id ? { ...t, name: trimmedName, order: newOrder } : t
      ));
    }
    setSavingId(null);
    setEditingId(null);
  };

  // ── Delete a custom stage ──
  const handleDelete = async (stage) => {
    if (stage.id <= 19) return; // safety: never delete defaults
    if (!window.confirm(`Delete stage "${stage.name}"? This cannot be undone.`)) return;
    if (useSupabaseDB) {
      await supabaseTasks.deleteTask(stage.id);
    } else {
      setTasks(tasks.filter(t => t.id !== stage.id));
    }
  };

  // Stage full names mapping
  const stageFullNames = {
    1: 'Advance',
    2: 'CD AD (Concept Design - Architecture)',
    3: 'CD ID (Concept Design - Interior)',
    4: 'SD AD (Schematic Design - Architecture)',
    5: 'SD ID (Schematic Design - Interior)',
    6: 'DD AD (Design Development - Architecture)',
    7: 'DD ID (Design Development - Interior)',
    8: 'AoC AD (Awarding of Contract - Architecture)',
    9: 'AoC ID (Awarding of Contract - Interior)',
    10: 'TD AD (Tender Design - Architecture)',
    11: 'TD ID (Tender Design - Interior)',
    12: 'GFC 1 (Good for Construction 1)',
    13: 'GFC 2 (Good for Construction 2)',
    14: 'GFC 3 (Good for Construction 3)',
    15: 'Completion',
    16: 'CC (Construction Coordination)',
    17: 'CT 1 (Construction Time 1)',
    18: 'CT 2 (Construction Time 2)',
    19: 'CT 3 (Construction Time 3)',
  };

  const handleResetDefaults = async () => {
    const defaultStages = [
      { id: 1, name: 'Advance' },
      { id: 2, name: 'CD AD' },
      { id: 3, name: 'CD ID' },
      { id: 4, name: 'SD AD' },
      { id: 5, name: 'SD ID' },
      { id: 6, name: 'DD AD' },
      { id: 7, name: 'DD ID' },
      { id: 8, name: 'AoC AD' },
      { id: 9, name: 'AoC ID' },
      { id: 10, name: 'TD AD' },
      { id: 11, name: 'TD ID' },
      { id: 12, name: 'GFC 1' },
      { id: 13, name: 'GFC 2' },
      { id: 14, name: 'GFC 3' },
      { id: 15, name: 'Completion' },
      { id: 16, name: 'CC' },
      { id: 17, name: 'CT 1' },
      { id: 18, name: 'CT 2' },
      { id: 19, name: 'CT 3' },
    ];
    if (window.confirm('Reset to default stages? This will keep your custom stages but ensure all defaults are present.')) {
      const existingIds = activeTasks.map(t => t.id);
      const newStages = defaultStages.filter(ds => !existingIds.includes(ds.id));
      if (newStages.length > 0) {
        if (useSupabaseDB) {
          for (const stage of newStages) {
            await supabaseTasks.createTask(stage);
          }
        } else {
          setTasks([...tasks, ...newStages]);
        }
        alert(`Added ${newStages.length} missing default stage(s).`);
      } else {
        alert('All default stages are already present.');
      }
    }
  };

  return (
    <div className="brio-flex brio-flex-col brio-analytics-font">
      {/* PAGE HEADER BAND: Full-width white background, centered content */}
      <div className="brio-pageband brio-pageband--header">
        <BrioFrame>
          <div className="brio-page-header__content">
            <div className="brio-page-header__left">

            </div>
            {(currentUser.role === 'admin' || currentUser.role === 'admin-pa') && (
              <div className="brio-page-header__right">
                <BrioActionGroup>
                  <button 
                    onClick={handleResetDefaults} 
                    className="brio-btn brio-btn-secondary"
                  >
                    Reset Defaults
                  </button>
                  <button 
                    onClick={() => setModal('addTask')} 
                    className="brio-btn brio-btn-primary"
                  >
                    <Plus className="w-4 h-4"/>Add Stage
                  </button>
                </BrioActionGroup>
              </div>
            )}
          </div>
        </BrioFrame>
      </div>

      {/* CONTENT AREA: Table wrapped in BrioFrame for 875px alignment */}
      <BrioFrame>
        <div className="brio-gap-s" style={{ paddingTop: 'var(--brio-space-s)' }}>
          {activeTasks.length > 0 ? (
            <BrioAnalyticsSurface padding="m">
              <BrioByCode code={PRESENTATION_CODE}>
                <BrioTable {...tableProps}>
                  <thead>
                    <tr>
                      <th style={{ width: 50 }} className="text-center">#</th>
                      <th>Stage</th>
                      <th className="text-center col-actions" style={{ width: 100 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedStages.map(t => {
                      const displayName = stageFullNames[t.id] || t.name;
                      const isDefault = t.id <= 16;
                      const isEditing = editingId === t.id;
                      const isSaving = savingId === t.id;
                      const orderValue = t.order != null ? t.order : (isDefault ? t.id : '');
                      
                      return (
                        <tr key={t.id} className="brio-row--hoverable">
                          {/* Order column */}
                          <td className="text-center">
                            {isEditing ? (
                              <input
                                type="number"
                                min="1"
                                value={editOrder}
                                onChange={(e) => setEditOrder(e.target.value)}
                                style={{
                                  width: '40px', padding: '2px 4px', fontSize: '11px',
                                  textAlign: 'center', border: '1px solid var(--brio-gold)',
                                  fontFamily: 'var(--brio-font-family)',
                                }}
                              />
                            ) : (
                              <span className="brio-text-grey" style={{ fontSize: '11px' }}>
                                {orderValue}
                              </span>
                            )}
                          </td>
                          {/* Stage name */}
                          <td>
                            {isEditing ? (
                              <input
                                type="text"
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(t); if (e.key === 'Escape') cancelEdit(); }}
                                className="brio-input"
                                style={{
                                  padding: '4px 8px', fontSize: '12px', width: '100%',
                                  border: '1px solid var(--brio-gold)',
                                  fontFamily: 'var(--brio-font-family)',
                                }}
                                autoFocus
                              />
                            ) : (
                              <div>
                                <span className="brio-font-medium">{isDefault ? displayName : t.name}</span>
                                {!isDefault && (
                                  <span className="brio-badge brio-badge--warning" style={{ marginLeft: '8px', fontSize: '9px' }}>Custom</span>
                                )}
                              </div>
                            )}
                          </td>
                          {/* Actions */}
                          <td className="text-center">
                            {(currentUser.role === 'admin' || currentUser.role === 'admin-pa') ? (
                              isEditing ? (
                                <BrioActionGroup center>
                                  <button
                                    onClick={() => saveEdit(t)}
                                    disabled={isSaving}
                                    className="brio-btn brio-btn-primary"
                                    style={{ padding: '2px 8px', fontSize: '10px' }}
                                    title="Save"
                                  >
                                    <Check className="w-3 h-3" />
                                  </button>
                                  <button
                                    onClick={cancelEdit}
                                    className="brio-btn brio-btn-secondary"
                                    style={{ padding: '2px 8px', fontSize: '10px' }}
                                    title="Cancel"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </BrioActionGroup>
                              ) : (
                                <BrioActionGroup center>
                                  <BrioIconButton
                                    icon="edit"
                                    variant="default"
                                    size="small"
                                    title="Edit stage"
                                    onClick={() => startEdit(t)}
                                  />
                                  {!isDefault && (
                                    <BrioIconButton
                                      icon="delete"
                                      variant="delete"
                                      size="small"
                                      title="Delete stage"
                                      onClick={() => handleDelete(t)}
                                    />
                                  )}
                                  {isDefault && (
                                    <span className="brio-badge brio-badge--pending" style={{ fontSize: '9px' }}>Default</span>
                                  )}
                                </BrioActionGroup>
                              )
                            ) : (
                              isDefault && <span className="brio-badge brio-badge--pending" style={{ fontSize: '9px' }}>Default</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </BrioTable>
              </BrioByCode>
            </BrioAnalyticsSurface>
          ) : (
            <BrioAnalyticsSurface padding="m">
              <div className="brio-text-center brio-analytics-muted" style={{ padding: '48px 0' }}>
                No stages added yet
              </div>
            </BrioAnalyticsSurface>
          )}

          {currentUser.role !== 'admin' && (
            <p className="brio-analytics-muted brio-mt-s">All users can add time to any stage. Contact admin to create new stages.</p>
          )}
        </div>
      </BrioFrame>
    </div>
  );
}
